export const ACTIVE_TRIP_GENERATION_KEY = "dople.active-trip-generation.v1";
export const TRIP_GENERATION_EVENT = "dople:trip-generation";

export function readActiveTripGeneration() {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(ACTIVE_TRIP_GENERATION_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

export function writeActiveTripGeneration(value) {
  if (typeof window === "undefined") return;
  try {
    if (value) {
      window.localStorage.setItem(ACTIVE_TRIP_GENERATION_KEY, JSON.stringify(value));
    } else {
      window.localStorage.removeItem(ACTIVE_TRIP_GENERATION_KEY);
    }
  } catch {
    // Global status is an affordance; the trip record remains authoritative.
  }
  window.dispatchEvent(new CustomEvent(TRIP_GENERATION_EVENT, { detail: value || null }));
}

export function tripGenerationRecordIsGone(error) {
  const status = Number(error?.response?.status || 0);
  return [403, 404, 410].includes(status);
}

export function tripGenerationStageCopy(stage = "") {
  const normalized = String(stage || "").toLowerCase();
  if (normalized === "researching") return "Researching real places, operators, and current context";
  if (normalized === "building_itinerary") return "Building the route and complete day-by-day itinerary";
  if (normalized === "checking_itinerary") return "Checking logistics, pacing, and repeated places";
  if (normalized === "complete") return "Your itinerary is ready";
  if (normalized === "failed") return "Your itinerary needs another pass";
  return "Preparing your personalized trip";
}

function validDate(value) {
  if (!value) return null;
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

export function estimatedTripDayCount({ startDate = "", endDate = "", tripType = "" } = {}) {
  const start = validDate(startDate);
  const end = validDate(endDate);
  if (start && end && end >= start) {
    return Math.max(1, Math.round((end - start) / 86400000) + 1);
  }
  switch (String(tripType || "").toLowerCase()) {
    case "day":
      return 1;
    case "weekend":
      return 2;
    case "short":
    case "flexible":
      return 4;
    case "full":
    default:
      return 7;
  }
}

export function countTripRouteStops(destination = "") {
  const text = String(destination || "").trim();
  if (!text) return 0;
  const parts = text
    .split(/\s*(?:->|→|⇒)\s*/)
    .map((part) => part.trim())
    .filter(Boolean);
  return parts.length;
}

export function tripGenerationEstimate({
  startDate = "",
  endDate = "",
  tripType = "",
  destination = "",
  structuredConstraints = {},
} = {}) {
  const constraints =
    structuredConstraints && typeof structuredConstraints === "object"
      ? structuredConstraints
      : {};
  const dayCount = estimatedTripDayCount({ startDate, endDate, tripType });

  // Calibrated from live Trip Project runs (p50 + buffer).
  // Longer multi-stop trips can take materially longer than short trips.
  // after prepare; old UI showed 7-11 and felt broken.
  let minMinutes;
  let maxMinutes;
  if (dayCount <= 2) {
    [minMinutes, maxMinutes] = [5, 8];
  } else if (dayCount <= 4) {
    [minMinutes, maxMinutes] = [8, 12];
  } else if (dayCount <= 7) {
    [minMinutes, maxMinutes] = [11, 16];
  } else if (dayCount <= 10) {
    [minMinutes, maxMinutes] = [14, 20];
  } else if (dayCount <= 14) {
    [minMinutes, maxMinutes] = [14, 20];
  } else if (dayCount <= 21) {
    [minMinutes, maxMinutes] = [17, 24];
  } else {
    [minMinutes, maxMinutes] = [20, 28];
  }

  const detailText = [
    destination,
    constraints.additional_context,
    constraints.guidance,
    constraints.travel_party,
  ]
    .filter(Boolean)
    .join(" ");
  const destinationCandidates = Array.isArray(constraints.destination_candidates)
    ? constraints.destination_candidates.length
    : 0;
  const routeStops = Math.max(
    countTripRouteStops(destination),
    Number(constraints.base_count || constraints.destination_count || 0) || 0
  );
  const complex =
    destinationCandidates > 1 ||
    routeStops > 1 ||
    detailText.length > 260 ||
    /\b(multi[- ]?city|road trip|event|concert|festival|accessible|wheelchair|allergy|dietary|children|kids|family reunion|group trip)\b/i.test(
      detailText
    );

  // Complexity adds a cushion above p50 rather than a token +1 minute.
  if (complex) {
    minMinutes += 2;
    maxMinutes += 4;
  }
  if (routeStops >= 4) {
    minMinutes += 2;
    maxMinutes += 3;
  }
  if (routeStops >= 6) {
    minMinutes += 1;
    maxMinutes += 2;
  }

  return {
    dayCount,
    complexity: complex ? "complex" : "standard",
    routeStops,
    minMinutes,
    maxMinutes,
    label: `${minMinutes}-${maxMinutes} minutes`,
  };
}
